import { useEffect, useState } from 'react';
import type { AuthMe } from '@cornerstone/shared';
import { API_BASE } from '../client/config';

// =============================================================================
// useAuthMe — /api/auth/me 단일 소스.
//
// 예전에는 useAdminRole·useAuthEmail·ActingBanner가 각자 /me를 때렸다(화면마다
// 3회). 여기서 모듈 단위로 한 번만 부르고 구독자에게 나눠 준다. 권한·대행 상태를
// 쓰는 모든 곳(useWriteGate, useBoOperator)이 같은 답을 본다.
// 2026-09-16 검수 S2 — 권한 없는 계정이 BO 전용 엔드포인트를 두드려 관리 이력이
// 403 로그로 채워지던 문제의 출발점도 여기다.
// =============================================================================

export type AuthMeState = {
  /** 아직 /me 응답 전 — 권한 판정을 미뤄야 한다. */
  loading: boolean;
  me: AuthMe | null;
  /** 마지막 권한 확인 결과. 일시 장애는 성공 캐시를 지우지 않는다. */
  status: 'checking' | 'ready' | 'unauthenticated' | 'forbidden' | 'unavailable';
};

export const AUTH_ME_UNAVAILABLE_MESSAGE = '권한 확인에 실패했습니다. 다시 시도합니다.';
const AUTH_ME_TIMEOUT_MS = 10_000;

let cache: AuthMeState = { loading: true, me: null, status: 'checking' };
let inflight: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;
const listeners = new Set<(state: AuthMeState) => void>();

function publish(next: AuthMeState): void {
  cache = next;
  listeners.forEach((fn) => fn(next));
}

function clearRetry(): void {
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = null;
}

function retryAfterTransientFailure(): void {
  if (retryTimer !== null) return;
  const delay = Math.min(1_000 * 2 ** retryAttempt, 30_000);
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void load();
  }, delay);
}

function unavailable(): void {
  // 기존에 확인된 신원만 유지한다. 첫 진입 실패에서는 me가 null이라 최소 권한으로
  // 남고, 401/403처럼 명시적으로 무효화된 세션은 이 경로로 오지 않는다.
  publish({ loading: false, me: cache.me, status: 'unavailable' });
  retryAfterTransientFailure();
}

function load(): Promise<void> {
  if (inflight) return inflight;
  const request = new AbortController();
  const timeout = setTimeout(() => request.abort(), AUTH_ME_TIMEOUT_MS);
  inflight = fetch(`${API_BASE}/api/auth/me`, { signal: request.signal })
    .then(async (r) => {
      if (r.ok) {
        const me = await r.json() as AuthMe | null;
        clearRetry();
        retryAttempt = 0;
        publish({ loading: false, me: me ?? null, status: 'ready' });
        return;
      }
      if (r.status === 401) {
        clearRetry();
        retryAttempt = 0;
        publish({ loading: false, me: null, status: 'unauthenticated' });
        return;
      }
      if (r.status === 403 || r.status === 428) {
        clearRetry();
        retryAttempt = 0;
        publish({ loading: false, me: null, status: 'forbidden' });
        return;
      }
      unavailable();
    })
    .catch(unavailable)
    .finally(() => {
      clearTimeout(timeout);
      inflight = null;
    });
  return inflight;
}

/** 로그인·대행 진입 등으로 신원이 바뀌었을 때 다시 읽는다. */
export function refreshAuthMe(): Promise<void> {
  clearRetry();
  publish({ loading: true, me: cache.me, status: 'checking' });
  return load();
}

/** 테스트 전용 — 모듈 캐시 초기화. */
export function resetAuthMeCache(): void {
  clearRetry();
  cache = { loading: true, me: null, status: 'checking' };
  inflight = null;
  retryAttempt = 0;
}

export function useAuthMe(): AuthMeState {
  const [state, setState] = useState<AuthMeState>(cache);

  useEffect(() => {
    listeners.add(setState);
    setState(cache);
    if (cache.loading) void load();
    return () => { listeners.delete(setState); };
  }, []);

  return state;
}
